(() => {
  'use strict';

  if (typeof window === 'undefined') return;
  if (window.MICJourneyDraft?.schemaVersion === 1) return;

  const SCHEMA_VERSION = 1;
  const STORAGE_KEY = 'mic.journeyDraft';
  const EVENT_NAME = 'mic:journey-draft-change';
  const SCOPES = new Set(['preview', 'committed']);
  const KINDS = new Set(['city', 'experience']);
  const LIMITS = Object.freeze({
    cities: 10,
    experiences: 60,
    interests: 20,
    tagsPerExperience: 12,
    cityName: 80,
    title: 160,
    imageAlt: 180,
    text: 180,
    cityDaysMin: 0.5,
    cityDaysMax: 30,
    journeyId: 128,
    accessToken: 1024
  });

  const subscribers = new Set();
  const writerId = 'mic-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);

  const cleanText = (value, max = LIMITS.text) => String(value ?? '')
    .replace(/[\u0000-\u001f\u007f<>]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);

  const cleanCityId = (value) => String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 64);

  const cleanOpaqueId = (value, max = 128) => {
    const candidate = String(value ?? '').trim();
    return /^[A-Za-z0-9][A-Za-z0-9:_-]*$/.test(candidate) ? candidate.slice(0, max) : '';
  };

  const cleanHandle = (value) => {
    const candidate = String(value ?? '').trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,100}$/.test(candidate) ? candidate : '';
  };

  const cleanExperienceId = (value) => {
    const candidate = String(value ?? '').trim().toUpperCase();
    return /^[A-Z0-9][A-Z0-9-]{1,63}$/.test(candidate) ? candidate : '';
  };

  const cleanHttpsUrl = (value) => {
    const candidate = String(value ?? '').trim();
    if (!candidate || candidate.length > 2048) return '';
    try {
      const parsed = new URL(candidate);
      return parsed.protocol === 'https:' ? parsed.href : '';
    } catch (error) {
      return '';
    }
  };

  const cleanDays = (value) => {
    const candidate = Number(value);
    if (!Number.isFinite(candidate) || candidate <= 0) return null;
    const rounded = Math.round(candidate * 2) / 2;
    return Math.min(LIMITS.cityDaysMax, Math.max(LIMITS.cityDaysMin, rounded));
  };

  const uniqueStrings = (values, maxItems, maxLength) => {
    const seen = new Set();
    const output = [];
    for (const raw of Array.isArray(values) ? values : []) {
      const value = cleanText(raw, maxLength);
      const key = value.toLocaleLowerCase();
      if (!value || seen.has(key)) continue;
      seen.add(key);
      output.push(value);
      if (output.length >= maxItems) break;
    }
    return output;
  };

  const normalizeCity = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const name = cleanText(raw.name ?? raw.city, LIMITS.cityName);
    const id = cleanCityId(raw.id ?? raw.cityId ?? name);
    if (!id || !name) return null;
    return { id, name };
  };

  const normalizeExperience = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const id = cleanOpaqueId(raw.id ?? raw.moduleId);
    const experienceId = cleanExperienceId(raw.experienceId);
    const handle = cleanHandle(raw.handle ?? raw.productHandle);
    const city = cleanText(raw.city ?? raw.cityName, LIMITS.cityName);
    const cityId = cleanCityId(raw.cityId ?? city);
    if (!cityId || (!id && !experienceId && !handle)) return null;

    const title = cleanText(raw.title, LIMITS.title) || cleanText(experienceId || handle || id, LIMITS.title);
    return {
      id: id || experienceId || handle,
      experienceId,
      handle,
      cityId,
      city,
      title,
      imageUrl: cleanHttpsUrl(raw.imageUrl ?? raw.image),
      imageAlt: cleanText(raw.imageAlt, LIMITS.imageAlt),
      tags: uniqueStrings(raw.tags, LIMITS.tagsPerExperience, 40)
    };
  };

  const experienceKey = (experience) => experience.experienceId || experience.handle || experience.id;

  const normalizeSelection = (raw) => {
    const input = raw && typeof raw === 'object' ? raw : {};
    const cityMap = new Map();
    for (const rawCity of Array.isArray(input.cities) ? input.cities : []) {
      const city = normalizeCity(rawCity);
      if (!city || cityMap.has(city.id)) continue;
      cityMap.set(city.id, city);
      if (cityMap.size >= LIMITS.cities) break;
    }

    const experiences = [];
    const experienceKeys = new Set();
    for (const rawExperience of Array.isArray(input.experiences) ? input.experiences : []) {
      const experience = normalizeExperience(rawExperience);
      if (!experience || !cityMap.has(experience.cityId)) continue;
      const key = experienceKey(experience);
      if (!key || experienceKeys.has(key)) continue;
      const city = cityMap.get(experience.cityId);
      experience.city = city.name;
      experienceKeys.add(key);
      experiences.push(experience);
      if (experiences.length >= LIMITS.experiences) break;
    }

    const cityDays = {};
    const rawDays = input.cityDays && typeof input.cityDays === 'object' ? input.cityDays : {};
    cityMap.forEach((_city, cityId) => {
      const days = cleanDays(rawDays[cityId]);
      if (days !== null) cityDays[cityId] = days;
    });

    return {
      cities: [...cityMap.values()],
      experiences,
      cityDays,
      interests: uniqueStrings(input.interests, LIMITS.interests, 40)
    };
  };

  const normalizeReference = (raw) => {
    const input = raw && typeof raw === 'object' ? raw : {};
    const journeyId = cleanOpaqueId(input.journeyId, LIMITS.journeyId);
    const tokenCandidate = String(input.accessToken ?? '').trim();
    const accessToken = tokenCandidate.length <= LIMITS.accessToken && !/[\s\u0000-\u001f\u007f]/.test(tokenCandidate)
      ? tokenCandidate
      : '';
    return { journeyId, accessToken };
  };

  const emptySelection = () => ({ cities: [], experiences: [], cityDays: {}, interests: [] });

  const emptyState = () => ({
    schemaVersion: SCHEMA_VERSION,
    revision: 0,
    updatedAt: '',
    writerId: '',
    preview: emptySelection(),
    committed: emptySelection(),
    reference: { journeyId: '', accessToken: '' }
  });

  const normalizeState = (raw) => {
    if (!raw || typeof raw !== 'object' || Number(raw.schemaVersion) !== SCHEMA_VERSION) return emptyState();
    return {
      schemaVersion: SCHEMA_VERSION,
      revision: Math.max(0, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(Number(raw.revision) || 0))),
      updatedAt: /^\d{4}-\d{2}-\d{2}T/.test(String(raw.updatedAt ?? '')) ? String(raw.updatedAt) : '',
      writerId: cleanOpaqueId(raw.writerId, 80),
      preview: normalizeSelection(raw.preview),
      committed: normalizeSelection(raw.committed),
      reference: normalizeReference(raw.reference)
    };
  };

  const clone = (value) => JSON.parse(JSON.stringify(value));

  const deepFreeze = (value) => {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    Object.freeze(value);
    Object.values(value).forEach(deepFreeze);
    return value;
  };

  const publicSnapshot = (state) => deepFreeze({
    schemaVersion: SCHEMA_VERSION,
    revision: state.revision,
    updatedAt: state.updatedAt,
    preview: clone(state.preview),
    committed: clone(state.committed),
    reference: {
      journeyId: state.reference.journeyId,
      hasAccessToken: Boolean(state.reference.accessToken)
    }
  });

  const readStorage = () => {
    try {
      const raw = window.localStorage?.getItem(STORAGE_KEY);
      return raw ? normalizeState(JSON.parse(raw)) : emptyState();
    } catch (error) {
      return emptyState();
    }
  };

  let state = readStorage();

  const writeStorage = () => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // The in-memory draft remains usable when storage is unavailable or full.
    }
  };

  const reportSubscriberError = (error) => {
    if (window.console?.error) window.console.error('[MICJourneyDraft] subscriber failed', error);
  };

  const notify = (source, reason) => {
    const snapshot = publicSnapshot(state);
    const metadata = deepFreeze({ source, reason });
    subscribers.forEach((subscriber) => {
      try {
        subscriber(snapshot, metadata);
      } catch (error) {
        reportSubscriberError(error);
      }
    });

    if (typeof window.CustomEvent === 'function' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new window.CustomEvent(EVENT_NAME, {
        detail: deepFreeze({ source, reason, snapshot })
      }));
    }
  };

  const transact = (reason, mutator) => {
    const next = normalizeState(clone(state));
    mutator(next);
    const normalized = normalizeState(next);
    normalized.revision = Math.min(Number.MAX_SAFE_INTEGER, state.revision + 1);
    normalized.updatedAt = new Date().toISOString();
    normalized.writerId = writerId;
    state = normalized;
    writeStorage();
    notify('local', reason);
    return publicSnapshot(state);
  };

  const assertScope = (scope) => {
    if (!SCOPES.has(scope)) throw new TypeError('scope must be "preview" or "committed"');
    return scope;
  };

  const assertKind = (kind) => {
    if (!KINDS.has(kind)) throw new TypeError('kind must be "city" or "experience"');
    return kind;
  };

  const set = (scope, patch) => {
    assertScope(scope);
    const input = patch && typeof patch === 'object' ? patch : {};
    return transact('set:' + scope, (next) => {
      next[scope] = normalizeSelection({
        cities: Object.hasOwn(input, 'cities') ? input.cities : next[scope].cities,
        experiences: Object.hasOwn(input, 'experiences') ? input.experiences : next[scope].experiences,
        cityDays: Object.hasOwn(input, 'cityDays') ? input.cityDays : next[scope].cityDays,
        interests: Object.hasOwn(input, 'interests') ? input.interests : next[scope].interests
      });
    });
  };

  const add = (scope, kind, value) => {
    assertScope(scope);
    assertKind(kind);
    return transact('add:' + scope + ':' + kind, (next) => {
      const selection = next[scope];
      if (kind === 'city') {
        const city = normalizeCity(value);
        if (city && !selection.cities.some((entry) => entry.id === city.id)) selection.cities.push(city);
      } else {
        const experience = normalizeExperience(value);
        if (!experience) return;
        if (!selection.cities.some((city) => city.id === experience.cityId)) {
          const city = normalizeCity({ id: experience.cityId, name: experience.city });
          if (city) selection.cities.push(city);
        }
        selection.experiences.push(experience);
      }
      next[scope] = normalizeSelection(selection);
    });
  };

  const remove = (scope, kind, identity) => {
    assertScope(scope);
    assertKind(kind);
    const key = String(identity ?? '').trim();
    return transact('remove:' + scope + ':' + kind, (next) => {
      if (kind === 'city') {
        const cityId = cleanCityId(key);
        next[scope].cities = next[scope].cities.filter((city) => city.id !== cityId);
        delete next[scope].cityDays[cityId];
        next[scope].experiences = next[scope].experiences.filter((experience) => experience.cityId !== cityId);
      } else {
        next[scope].experiences = next[scope].experiences.filter((experience) => (
          experience.id !== key && experience.experienceId !== key && experience.handle !== key
        ));
      }
      next[scope] = normalizeSelection(next[scope]);
    });
  };

  const replace = (scope, selection) => {
    assertScope(scope);
    return transact('replace:' + scope, (next) => {
      next[scope] = normalizeSelection(selection);
    });
  };

  const setCityDays = (scope, cityIdentity, daysValue) => {
    assertScope(scope);
    const cityId = cleanCityId(cityIdentity);
    return transact('days:' + scope, (next) => {
      if (!next[scope].cities.some((city) => city.id === cityId)) return;
      const days = cleanDays(daysValue);
      if (days === null) delete next[scope].cityDays[cityId];
      else next[scope].cityDays[cityId] = days;
      next[scope] = normalizeSelection(next[scope]);
    });
  };

  const mergeSelections = (base, incoming) => normalizeSelection({
    cities: [...base.cities, ...incoming.cities],
    experiences: [...base.experiences, ...incoming.experiences],
    // A recommendation can add new cities, but must not overwrite days the user already edited.
    cityDays: { ...incoming.cityDays, ...base.cityDays },
    interests: [...base.interests, ...incoming.interests]
  });

  const commitPreview = (options = {}) => {
    const strategy = options.strategy === 'merge' ? 'merge' : 'replace';
    const clearPreview = options.clearPreview !== false;
    return transact('commit-preview', (next) => {
      next.committed = strategy === 'merge'
        ? mergeSelections(next.committed, next.preview)
        : normalizeSelection(next.preview);
      if (clearPreview) next.preview = emptySelection();
    });
  };

  const setReference = (reference) => transact('set-reference', (next) => {
    next.reference = normalizeReference(reference);
  });

  const getReference = () => clone(state.reference);

  const getSnapshot = () => publicSnapshot(state);

  const subscribe = (listener, options = {}) => {
    if (typeof listener !== 'function') throw new TypeError('listener must be a function');
    subscribers.add(listener);
    if (options.immediate !== false) listener(getSnapshot(), deepFreeze({ source: 'initial', reason: 'subscribe' }));
    return () => subscribers.delete(listener);
  };

  const clear = (scope = 'all') => {
    if (scope !== 'all') assertScope(scope);
    return transact('clear:' + scope, (next) => {
      if (scope === 'all') {
        next.preview = emptySelection();
        next.committed = emptySelection();
        next.reference = { journeyId: '', accessToken: '' };
      } else {
        next[scope] = emptySelection();
      }
    });
  };

  window.addEventListener?.('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    let incoming = emptyState();
    if (event.newValue) {
      try {
        incoming = normalizeState(JSON.parse(event.newValue));
      } catch (error) {
        incoming = emptyState();
      }
    }
    if (incoming.writerId && incoming.writerId === writerId) return;
    state = incoming;
    notify('storage', 'sync');
  });

  const api = Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    storageKey: STORAGE_KEY,
    eventName: EVENT_NAME,
    limits: LIMITS,
    set,
    add,
    remove,
    replace,
    setCityDays,
    commitPreview,
    setReference,
    // Direct API access only. Snapshots, subscribers and CustomEvents always redact this token.
    getReference,
    subscribe,
    getSnapshot,
    clear
  });

  Object.defineProperty(window, 'MICJourneyDraft', {
    value: api,
    configurable: false,
    enumerable: true,
    writable: false
  });
})();
